import React from "react";
import { Button } from "@hdir/ui/button";
import { TabGroup, Tab } from "@hdir/ui/tab";
import { Select, SelectOption } from "@hdir/ui/select";
import { faSliders } from "@fortawesome/pro-light-svg-icons";

import FontAwesomeIconWrapper from "../../../react4xp/shared/FontAwesomeIconWrapper/FontAwesomeIconWrapper";
import Filter from "../../../react4xp/shared/Filter/Filter";
import ListSearchResultContainer from "/react4xp/shared/ListSearch/ListSearchResultContainer";
import ListSearchSearchComponent from "/react4xp/shared/ListSearch/ListSearchSearchComponent";

import type { ListSearchProps } from "./type.d";
import { useListSearchFilter } from "/react4xp/shared/ListSearch/useListSearchFilter";
import { useListSearchData } from "/react4xp/shared/ListSearch/useListSearchData";

function ListSearch(props: Readonly<ListSearchProps>) {
  const {
    cardBaseId,
    filterCloseable,
    filterFromParams,
    filterLists,
    groupByField,
    hideSearchField,
    isEditMode,
    localizedForListSearch,
    paginationSize,
    queryObject,
    searchQueryFromParams,
    serviceUrl,
    sortByOptions,
    tabsData,
    title,
    useTabs,
    useNormerendeCollection
  }: ListSearchProps = props;

  // ── Filter state ────────────────────────────────────────────
  const {
    currentFilter,
    isFilterOpen,
    setIsFilterOpen,
    countChecked,
    setCountChecked,
    getFilterQuery,
    handleFilterSelected,
    resetDisplayedFilter
  } = useListSearchFilter({ filterLists, filterFromParams });

  // ── Data fetching & list state ──────────────────────────────
  const {
    isInitialPageLoad,
    isFetching,
    list,
    tabsLists,
    currentTotalCount,
    searchInputRef,
    setSearchQuery,
    setActiveTabIndex,
    fetchListOnSearchInput,
    fetchWithFilter,
    handleFetchMore,
    handleSortSelected
  } = useListSearchData({
    cardBaseId,
    filterFromParams,
    groupByField,
    paginationSize,
    queryObject,
    searchQueryFromParams,
    serviceUrl,
    tabsData,
    useTabs,
    useNormerendeCollection,
    getFilterQuery
  });

  // ── Filter actions that need both hooks ─────────────────────
  const onFetchWithFilter = async(emptyFilter?: boolean) => {
    await fetchWithFilter(emptyFilter);
    if (!emptyFilter) setIsFilterOpen((prev) => !prev);
  };

  const resetFilter = (): void => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    resetDisplayedFilter();
    fetchWithFilter(true);
  };

  return (
    <div className="w-full py-design-component-mobile tablet:py-design-component-tablet desktop:py-design-component-desktop">
      { !hideSearchField && (<ListSearchSearchComponent searchInputRef={searchInputRef} fetchListOnSearchInput={fetchListOnSearchInput} setSearchQuery={setSearchQuery} localizedForListSearch={localizedForListSearch} title={title} />) }
      { hideSearchField && (<h2>{title}</h2>) }
      <div className="flex flex-col mt-md tablet:mt-xl">
        { (filterLists.length > 0 && !filterCloseable) && (
          <Filter
            countChecked={countChecked}
            currentFilter={currentFilter}
            filterCloseable={filterCloseable}
            filterLists={filterLists}
            handleCheckboxChange={handleFilterSelected}
            isEditMode={isEditMode}
            isFilterOpen={isFilterOpen}
            localizedForListSearch={localizedForListSearch}
            onFetchWithFilter={onFetchWithFilter}
            onResetFilter={resetFilter}
            setCountChecked={setCountChecked}
            setIsFilterOpen={setIsFilterOpen}
          />
        )}

        { /* Helsecore slår ihjel hdir styling med flex-col */ }
        <div className={`${useTabs ? "justify-end" : "justify-between"} gap-md`}>
          { !useTabs
            && (
              <span aria-live="polite" aria-atomic="true" aria-relevant="all" className="sr-only">
                {`${currentTotalCount} treff funnet for ${searchInputRef?.current?.value}. Viser ${list?.length} treff`}
              </span>
            )}

          { (filterLists.length > 0 && filterCloseable) && (
            <Button onClick={() => setIsFilterOpen((prev) => !prev)} className="w-32 flex justify-self-end">
              <FontAwesomeIconWrapper icon={faSliders} />
              {`${localizedForListSearch?.filter ?? "Filtrer"} ${countChecked > 0 ? `(${countChecked})` : ""}`}
            </Button>
          )}

          { (sortByOptions.length > 1 || !useTabs) && (
            <div className={`mt-lg flex flex-row items-center ${useTabs ? "justify-end" : "justify-between"}`}>
              { !useTabs && <span aria-hidden="true">{`Viser ${list?.length || 0} av ${currentTotalCount || 0} treff`}</span>}

              { sortByOptions.length > 1 && (
                <Select
                  defaultValue={sortByOptions[0]}
                  className="sortSelector flex-row items-center"
                  label={localizedForListSearch?.sortBy || "Sorter etter:"}
                  getDisplayName={(option: { name: string; field: (string | undefined) }): string => option.name}
                  onChange={(e) => {
                    handleSortSelected(e?.field ?? "");
                  }}
                  fullWidth={false}
                  name="selector"
                >
                  { sortByOptions.map((option, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <SelectOption key={`${option.name}-${index}`} value={option}>
                      {option.name}
                    </SelectOption>
                  ))}
                </Select>
              )}
            </div>
          )}

        </div>

        { (filterLists.length > 0 && filterCloseable) && (
          <Filter
            countChecked={countChecked}
            currentFilter={currentFilter}
            filterCloseable={filterCloseable}
            filterLists={filterLists}
            handleCheckboxChange={handleFilterSelected}
            isEditMode={isEditMode}
            isFilterOpen={isFilterOpen}
            localizedForListSearch={localizedForListSearch}
            onFetchWithFilter={onFetchWithFilter}
            onResetFilter={resetFilter}
            setCountChecked={setCountChecked}
            setIsFilterOpen={setIsFilterOpen}
          />
        )}
      </div>

      {(useTabs && tabsLists?.length > 0) ? (
        <TabGroup className="resultTabs mt-md">
          {tabsLists?.map((tab, idx) => (
            <Tab id={`tabs-${tab.tabId}`} tabLabel={`${tab?.title} (${tab?.totalCount})`} key={tab.tabId} onClick={() => setActiveTabIndex(idx)}>
              <span aria-live="polite" aria-atomic="true" aria-relevant="all" className="sr-only">
                {`${tab?.totalCount} treff funnet for ${searchInputRef?.current?.value}. Viser ${tab?.list?.length} treff`}
              </span>
              <ListSearchResultContainer
                cardBaseId={cardBaseId}
                paginationSize={paginationSize}
                isInitialPageLoad={isInitialPageLoad}
                isFetching={isFetching}
                list={tab?.list}
                localizedForListSearch={localizedForListSearch}
              />

              {(tab?.list?.length !== 0 && tab?.totalCount > tab?.list?.length) && (
                <div className="flex mt-lg justify-center">
                  <Button
                    size="medium"
                    variant="secondary"
                    onClick={() => handleFetchMore(tab.tabId)}
                  >
                    {localizedForListSearch?.showMore ?? "Vis flere"}
                  </Button>
                </div>
              )}
            </Tab>
          ))}
        </TabGroup>
      ) : (
        <>
          <ListSearchResultContainer
            cardBaseId={cardBaseId}
            paginationSize={paginationSize}
            isInitialPageLoad={isInitialPageLoad}
            isFetching={isFetching}
            list={list}
            localizedForListSearch={localizedForListSearch}
          />
          {(currentTotalCount > list?.length) && (
            <div className="flex mt-lg justify-center">
              <Button
                size="medium"
                variant="secondary"
                onClick={() => handleFetchMore()}
              >
                {localizedForListSearch?.showMore ?? "Vis flere"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function(props: Readonly<ListSearchProps>) {
  return <ListSearch {...props} />;
}
